"use client";

/**
 * Mounts its children only once their box comes within `rootMargin` of
 * the viewport; until then a placeholder with `className` holds the
 * space. For heavy below-the-fold widgets (ApexCharts is ~135 KB gzipped):
 * next/dynamic alone still fetched and ran the chunk right after
 * hydration, during page load, wherever the chart sat on the page.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";

export default function WhenVisible({
  className,
  children,
  rootMargin = "400px",
}: {
  className: string;
  children: ReactNode;
  rootMargin?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [near, setNear] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (near || !el) return;
    if (typeof IntersectionObserver === "undefined") { setNear(true); return; }
    const io = new IntersectionObserver(
      (entries) => { if (entries.some((e) => e.isIntersecting)) setNear(true); },
      { rootMargin },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [near, rootMargin]);
  if (near) return <>{children}</>;
  return <div ref={ref} className={className} aria-hidden />;
}
